import { useMemo, useState } from 'preact/hooks';
import { renderSVG } from 'uqr';
import { QUESTIONS_PER_PLAYER_OPTIONS, type Intensity, type Pace, type RoomSettings } from '@gazette/shared';
import { t, tn } from '../../i18n/index.ts';
import { actions, host, isHost, joinUrl, publicConfig, view } from '../../net/client.ts';
import { ErrorNotice, GameHeader } from '../components/Chrome.tsx';
import { Icon } from '../components/Icon.tsx';
import { Portrait } from '../components/Portrait.tsx';

function Segmented<T extends string | number>({
  legend,
  value,
  options,
  disabled,
  onChange,
}: {
  legend: string;
  value: T;
  options: { value: T; label: string }[];
  disabled: boolean;
  onChange: (value: T) => void;
}) {
  return (
    <fieldset class="segmented" disabled={disabled}>
      <legend class="field__label">{legend}</legend>
      <div class="segmented__options">
        {options.map((option) => (
          <button type="button" aria-pressed={option.value === value} onClick={() => option.value !== value && onChange(option.value)}>
            {option.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function Switch({ label, help, checked, disabled, onChange }: { label: string; help?: string; checked: boolean; disabled: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label class={`switch ${disabled ? 'is-disabled' : ''}`}>
      <input type="checkbox" role="switch" checked={checked} disabled={disabled} onChange={(event) => onChange(event.currentTarget.checked)} />
      <span class="switch__track" aria-hidden="true" />
      <span class="switch__text">
        <span class="switch__label">{label}</span>
        {help && <span class="switch__help">{help}</span>}
      </span>
    </label>
  );
}

function Settings({ settings, editable }: { settings: RoomSettings; editable: boolean }) {
  const update = (patch: Partial<RoomSettings>) => void actions.settings(patch);
  const intensities: Intensity[] = ['soft', 'spicy', 'unfiltered'];
  const paces: Pace[] = ['relaxed', 'normal', 'fast'];
  return (
    <section class="settings" aria-labelledby="settings-title">
      <h3 id="settings-title" class="section-title">
        {editable ? t('settings.title') : t('settings.readonly')}
      </h3>
      <Segmented
        legend={t('settings.intensity')}
        value={settings.intensity}
        disabled={!editable}
        options={intensities.map((value) => ({ value, label: t(`settings.intensity.${value}`) }))}
        onChange={(intensity) => update({ intensity })}
      />
      <p class="settings__help">{t(`settings.intensity.help.${settings.intensity}`)}</p>
      {settings.intensity !== 'soft' && (
        <Switch label={t('settings.adults')} checked={settings.adultsConfirmed} disabled={!editable} onChange={(adultsConfirmed) => update({ adultsConfirmed })} />
      )}
      <div class="settings__row">
        <Segmented
          legend={t('settings.questions')}
          value={settings.questionsPerPlayer}
          disabled={!editable}
          options={QUESTIONS_PER_PLAYER_OPTIONS.map((value) => ({ value, label: String(value) }))}
          onChange={(questionsPerPlayer) => update({ questionsPerPlayer })}
        />
        <Segmented
          legend={t('settings.pace')}
          value={settings.pace}
          disabled={!editable}
          options={paces.map((value) => ({ value, label: t(`settings.pace.${value}`) }))}
          onChange={(pace) => update({ pace })}
        />
      </div>
      <Switch label={t('settings.corbeau')} help={t('settings.corbeau.help')} checked={settings.corbeau} disabled={!editable} onChange={(corbeau) => update({ corbeau })} />
      <Switch
        label={t('settings.typography')}
        help={t('settings.typography.help')}
        checked={settings.uniformTypography}
        disabled={!editable}
        onChange={(uniformTypography) => update({ uniformTypography })}
      />
    </section>
  );
}

function Invite({ code }: { code: string }) {
  const [showQr, setShowQr] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = joinUrl(code);
  const qr = useMemo(() => renderSVG(url, { ecc: 'M', border: 2, pixelSize: 8, blackColor: '#15171b', whiteColor: '#e7e3d6' }), [url]);
  const localOnly = !publicConfig.value?.publicUrl && ['localhost', '127.0.0.1'].includes(location.hostname);

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: t('app.name'), url });
        return;
      } catch {
        // Dismissed share sheet: fall back to copying below.
      }
    }
    await navigator.clipboard?.writeText(url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div class="invite">
      <p class="invite__code">
        {t('lobby.case')} <strong>{code}</strong>
      </p>
      <div class="invite__actions">
        <button type="button" class="btn btn--secondary btn--small" onClick={() => void share()}>
          <Icon name={copied ? 'check' : 'share'} size={18} />
          {copied ? t('lobby.copied') : t('lobby.share')}
        </button>
        <button type="button" class="btn btn--secondary btn--small" aria-expanded={showQr} onClick={() => setShowQr(!showQr)}>
          <Icon name="qr" size={18} />
          {showQr ? t('lobby.qr.hide') : t('lobby.qr.show')}
        </button>
      </div>
      {showQr && <div class="invite__qr" role="img" aria-label={t('lobby.qr.alt', { code })} dangerouslySetInnerHTML={{ __html: qr }} />}
      {localOnly && <p class="invite__hint">{t('lobby.lanHint')}</p>}
    </div>
  );
}

export function Lobby() {
  const current = view.value!;
  const hosting = isHost.value;
  const missing = Math.max(0, current.config.minPlayers - current.players.length);
  const full = current.players.length >= current.config.maxPlayers;
  const blockedByAge = current.settings.intensity !== 'soft' && !current.settings.adultsConfirmed;

  return (
    <>
      <GameHeader />
      <section class="sheet sheet--lobby" data-occlude aria-labelledby="suspects-title">
        <Invite code={current.code} />

        <h2 id="suspects-title" class="section-title">
          {t('lobby.suspects')} <span class="section-title__count">{current.players.length} / {current.config.maxPlayers}</span>
        </h2>
        <ul class="suspects">
          {current.players.map((player) => {
            const mine = player.id === current.meId;
            return (
              <li class={`suspect-row ${player.online ? '' : 'is-offline'}`} key={player.id}>
                <Portrait seed={player.portrait} width={44} />
                <span class="suspect-row__name">{player.name}</span>
                <span class="suspect-row__tags">
                  {mine && <span class="tag">{t('lobby.you')}</span>}
                  {player.isHost && (
                    <span class="tag tag--host">
                      <Icon name="host" size={14} />
                      {t('lobby.host')}
                    </span>
                  )}
                  {player.isBot && <span class="tag">{t('lobby.bot')}</span>}
                  {!player.online && <span class="tag">{t('lobby.offline')}</span>}
                </span>
                {mine && (
                  <button type="button" class="icon-btn" onClick={() => void actions.rerollPortrait()} aria-label={t('lobby.reroll')} title={t('lobby.reroll')}>
                    <Icon name="reroll" />
                  </button>
                )}
                {hosting && !mine && (
                  <button type="button" class="icon-btn" onClick={() => void actions.kick(player.id)} aria-label={t('lobby.kick', { name: player.name })}>
                    <Icon name="close" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
        {hosting && current.config.botsEnabled && !full && (
          <button type="button" class="btn btn--secondary btn--small" onClick={() => void actions.addBot()}>
            <Icon name="bot" size={18} />
            {t('lobby.addBot')}
          </button>
        )}

        <Settings settings={current.settings} editable={hosting} />

        <div class="sheet__footer">
          <ErrorNotice />
          {missing > 0 && <p class="hint">{tn('lobby.need', missing)}</p>}
          {hosting ? (
            <button type="button" class="btn btn--primary btn--wide" disabled={missing > 0 || blockedByAge} onClick={() => void actions.start()}>
              {t('lobby.start')}
            </button>
          ) : (
            <p class="hint">{t('lobby.waitHost', { name: host.value?.name ?? '' })}</p>
          )}
        </div>
      </section>
    </>
  );
}
